<template>
  <div id="feature-filters">
    <div class="top-block">
      <!-- FILTERS -->
      <h4 class="ui horizontal fitted divider">
        Filtres
      </h4>
      <section
        id="form-filters"
        class="ui small form"
      >
        <!-- Search by NAME -->
        <div
          id="name"
          :class="['field', { 'disabled': !isOnline }]"
        >
          <label for="title">Nom</label>
          <div class="ui left icon input">
            <input
              :value="form.title"
              type="text"
              name="title"
              placeholder="Chercher un signalement..."
              @input="updateFilter('title', $event.target.value)"
            >
            <i class="search icon" />
          </div>
        </div>
        <!-- Filter by FEATURE TYPE -->
        <div
          id="type"
          :class="['field', { 'disabled': !isOnline }]"
        >
          <label>Type</label>
          <Multiselect
            v-model="selectedFeatureType"
            :options="featureTypeOptions"
            :multiple="true"
            :searchable="false"
            :close-on-select="false"
            :show-labels="false"
            placeholder="Sélectionner un type"
            track-by="value"
            label="name"
          />
        </div>
        <!-- Filter by STATUS -->
        <div
          v-if="!onlyPublishedStatus"
          id="statut"
          :class="['field', { 'disabled': !isOnline }]"
        >
          <label>Statut</label>
          <Multiselect
            v-model="selectedStatus"
            :options="statusOptions"
            :multiple="true"
            :searchable="false"
            :close-on-select="false"
            :show-labels="false"
            placeholder="Sélectionner un statut"
            track-by="value"
            label="name"
          />
        </div>
        <!-- Filter by CREATOR -->
        <div
          id="creator"
          data-test="form-creator"
          :class="['field', { 'disabled': !isOnline }]"
        >
          <label for="creator-id">Créé par</label>
          <ProjectMemberSelect
            id="creator-id"
            :selected-user-id="form.creator_id"
            @update:user="updateFilter('creator_id', $event)"
          />
        </div>
        <!-- Filters CREATION DATE -->
        <div class="field">
          <label>Date de création</label>
          <div :class="['two fields', { 'disabled': !isOnline }]">
            <div
              id="date-creation-start"
              data-test="form-date-creation-start"
              class="field"
            >
              <div class="ui labeled input">
                <div class="ui label">
                  De
                </div>
                <input
                  id="created-on--gte"
                  :value="form.created_on__gte"
                  type="date"
                  name="created_on__gte"
                  @change="updateFilter('created_on__gte', $event.target.value)"
                >
              </div>
            </div>
            <div
              id="date-creation-end"
              data-test="form-date-creation-end"
              class="field"
            >
              <div class="ui labeled input">
                <div class="ui label">
                  à
                </div>
                <input
                  id="created-on--lte"
                  :value="form.created_on__lte"
                  type="date"
                  name="created_on__lte"
                  @change="updateFilter('created_on__lte', $event.target.value)"
                >
              </div>
            </div>
          </div>
        </div>
        <!-- Filter by custom ATTRIBUTES -->
        <div
          id="button-add-filters"
          :class="['ui floating dropdown labeled search icon button fluid',
                   { 'disabled': customFilters.length >= 5 }]"
          @click="showFiltersOptions = !showFiltersOptions"
        >
          <i class="plus icon" />
          <span class="text">Ajouter un filtre sur champ personnalisé</span>
          <div :class="['menu', {'visible': showFiltersOptions}]">
            <div
              v-for="customField in projectCustomFields"
              :key="`${customField.name}-${customField.feature_type_slug}`"
              :class="[
                'item', {
                  'disabled': customFilters.some((el) => el.name === customField.name &&
                    el.feature_type_slug === customField.feature_type_slug)
                }
              ]"
              @click.stop="addCustomFilter(customField)"
            >
              {{ customField.label }}
            </div>
          </div>
        </div>
      
        <div
          v-for="filter in customFilters"
          :key="`${filter.name}-${filter.feature_type_slug}`"
          :class="['field', { 'disabled': !isOnline }]"
        >
          <label>{{ filter.label }}</label>
          <Multiselect
            v-if="['list', 'multi_choices_list'].includes(filter.field_type)"
            v-model="filter.value"
            :options="filter.options"
            :show-labels="false"
            placeholder="Sélectionner une valeur"
            @input="updateParentCustomFilter"
          />

          <Multiselect
            v-else-if="filter.field_type === 'boolean'"
            :value="booleanOptions.find(el => el.value === filter.value) || booleanOptions[0]"
            :options="booleanOptions"
            :searchable="false"
            :show-labels="false"
            placeholder="Sélectionner une valeur"
            track-by="value"
            label="label"
            @select="updateCustomFilter($event.value, filter.name)"
          />

          <div
            v-else-if="['char', 'text', 'integer', 'decimal'].includes(filter.field_type)"
            class="ui left icon input"
          >
            <input
              :value="filter.value"
              type="text"
              name="title"
              placeholder="Chercher une valeur..."
              @input="updateCustomFilter($event.target.value, filter.name)"
            >
            <i class="search icon" />
          </div>

          <ExtraForm
            v-else
            ref="extraForm"
            :field="{
              ...filter,
              'disabled': !isOnline,
            }"
            :use-value-only="true"
            :hide-label="true"
            @update:value="updateCustomFilter($event, filter.name)"
          />
          <i
            aria-hidden="true"
            class="white x icon"
            @click="removeCustomFilter(filter.name)"
          />
        </div>
      </section>
    </div>
  
    <button
      id="reset-filters"
      type="button"
      class="ui teal fluid icon button"
      @click="resetFilters"
    >
      <i
        aria-hidden="true"
        class="white undo icon"
      /> Réinitialiser les filtres
    </button>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import Multiselect from 'vue-multiselect';

import ProjectMemberSelect from '@/components/ProjectMemberSelect';
import ExtraForm from '@/components/ExtraForm';
import { statusChoices } from '@/utils';


export default {
  
  name: 'FeaturesListAndMapFilters',
  
  components: {
    ExtraForm,
    Multiselect,
    ProjectMemberSelect,
  },
  
  props: {
    form: {
      type: Object,
      default: () => {}
    },
  },
  
  data() {
    return {
      booleanOptions: [
        { label: '', value: '' },
        { label: 'Oui', value: true },
        { label: 'Non', value: false }
      ],
      customFilters: [],
      lat: null,
      lng: null,
      showFiltersOptions: false,
      zoom: null,
    };
  },

  computed: {
    ...mapState([
      'configuration',
      'isOnline'
    ]),
    ...mapState('feature-type', [
      'feature_types',
    ]),
    ...mapState('projects', [
      'project',
    ]),

    featureTypeTitles() {
      return this.feature_types.map((el) => el.title);
    },
    featureTypeOptions() {
      return this.feature_types.map((el) => ({ name: el.title, value: el.slug }));
    },
    statusOptions() {
      //* if project is not moderate, remove pending status
      return statusChoices.filter((el) =>
        this.project && this.project.moderation ? true : el.value !== 'pending'
      );
    },
    projectCustomFields() {
      // If this.form.feature_type_slug is empty or undefined, do not filter
      const filteredFeatureTypes = this.form.feature_type_slug?.length
        ? this.feature_types.filter(el => this.form.feature_type_slug.includes(el.slug))
        : this.feature_types;

      return filteredFeatureTypes
        .flatMap(el =>
          (el.customfield_set || []).map(field => ({
            ...field,
            feature_type_slug: el.slug, // Add the slug
            feature_type_title: el.title, // Add the title
          }))
        )
        .sort((a, b) => a.label.localeCompare(b.label));
    },

    onlyPublishedStatus() {
      return this.configuration?.VUE_APP_ONLY_PUBLISHED_STATUS;
    },

    selectedFeatureType: {
      get() {
        return this.form.feature_type_slug.map(type => this.featureTypeOptions.find(el => el.value === type)) || '';
      },
      set(newValue) {
        //* set the value selected in the dropdown
        this.updateFilter('feature_type_slug', newValue);
      }
    },

    selectedStatus: {
      get() {
        return this.form.status__value.map(status => this.statusOptions.find(el => el.value === status)) || '';
      },
      set(newValue) {
        //* set the value selected in the dropdown
        this.updateFilter('status__value', newValue);
      }
    },
  },

  watch: {
    'form.feature_type_slug': {
      handler(newFeatureTypeSlugs) {
        // If the selection has changed
        if (newFeatureTypeSlugs && newFeatureTypeSlugs.length > 0) {
          // Filter customFilters to keep only those whose feature_type_slug is still selected
          this.customFilters = this.customFilters.filter(filter =>
            newFeatureTypeSlugs.includes(filter.feature_type_slug)
          );
          this.updateParentCustomFilter();
        }
      },
      immediate: false, // Do not trigger on mount
    },
  },

  mounted() {
    window.addEventListener('mousedown', this.clickOutsideDropdowns);
  },

  destroyed() {
    window.removeEventListener('mousedown', this.clickOutsideDropdowns);
  },

  methods: {
    updateFilter(fieldName, newValue) {
      this.$emit('set-filter', { [fieldName]: newValue });
    },

    addCustomFilter(params) {
      const maxFilterLimit = 5;
      // Check if filter already exists and if the maximum allowed filters is reached
      if (!this.customFilters.some(el => el.name === params.name) && this.customFilters.length < maxFilterLimit) {
        const as_range = ['date'/* , 'decimal', 'integer' */].includes(params.field_type);
        this.customFilters.push({
          ...params,  // Extend all properties from `params`
          as_range,
          value: as_range ? ['', ''] : null,
        });
      }
      // Close the add filter dropdown
      this.showFiltersOptions = false;
    },

    updateParentCustomFilter() {
      this.$emit('set-custom-filter', this.customFilters);
    },  
    
    updateCustomFilter(newValue, filterName) {
      const filter = this.customFilters.find(el => el.name === filterName);
      if (filter) {
        filter.value = newValue;
        this.updateParentCustomFilter();
      }
    },
    
    removeCustomFilter(name) {
      this.customFilters = this.customFilters.filter(el => el.name !== name);
      this.updateParentCustomFilter();
    },  

    resetFilters() {
      this.customFilters = [];
      this.$emit('reset-form');
    },  

    clickOutsideDropdowns(e) {
      if (!e.target.closest('#button-add-filters')) {
        this.showFiltersOptions = false;
      }
    },
  }

};
</script>

<style lang="less" scoped>
#feature-filters {
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  .no-padding-mobile {
    width: 100%;
    margin-left: 25%;
  }

  .divider {
    padding-top: 0;
  }

  .field {
    position: relative;
    i.x.icon {
      position: absolute;
      top: 0;
      right: 0;
      &:hover {
        cursor: pointer;
        color: red;
      }
    }
  }

  .ui.floating.dropdown {
    margin-bottom: 1em;
    text-align: center;
    span:hover {
      cursor: pointer;
    }
    .menu.visible {
      display: block;
    }
  }
  #reset-filters {
    margin: 1em 0;
  }
  @media screen and (min-width: 768px) {
    #reset-filters {
      margin-bottom: 0;
    }
  }
}
</style>

<style>
#form-filters .multiselect__tags {
  white-space: normal !important;
}

#form-filters .multiselect__tag {
  background: var(--primary-color, #008c86) !important;
}

#form-filters .multiselect__tag-icon:focus,
#form-filters .multiselect__tag-icon:hover {
  background: var(--primary-highlight-color, #006f6a) !important;
}

#form-filters .multiselect__tag-icon:not(:hover)::after {
  color: var(--primary-highlight-color, #006f6a);
  filter: brightness(0.6);
}

#form-filters .multiselect__option--selected:not(:hover) {
  background-color: #e8e8e8 !important;
}
</style>